<template lang="pug">
  v-card
    v-card-title
      h2.card-title {{ isEditMode ? $t('base.edit_user') : $t('base.add_user') }}
    v-card-content
      form(autocomplete="off")
        .cols-x
          .cols-x__2
            v-input.mb-3(
              :label="$t('base.first_name')"
              :hint="errorMessage('first_name')"
              :invalid="!!errorMessage('first_name')"
              :focus="true"
              v-model="form.first_name"
              @input="onInput('first_name')")
            v-input.mb-3(
              :label="$t('base.last_name')"
              :hint="errorMessage('last_name')"
              :invalid="!!errorMessage('last_name')"
              v-model="form.last_name"
              @input="onInput('last_name')")
            v-input.mb-3(
              :label="$t('base.phone')"
              :hint="errorMessage('phone')"
              :invalid="!!errorMessage('phone')"
              v-model="form.phone"
              @input="onInput('phone')")
            v-input.mb-3(
              :label="$t('base.email')"
              :hint="errorMessage('email')"
              :invalid="!!errorMessage('email')"
              v-model="form.email"
              @input="onInput('email')")
            v-password-field(
              v-if="!isEditMode"
              :label="$t('base.password')"
              :hint="errorMessage('password')"
              :invalid="!!errorMessage('password')"
              v-model="form.password"
              @input="onInput('password')")
          .cols-x__2
            v-select.mb-3(
              :label="$t('base.lang')"
              :options="langs"
              item-value="key"
              v-model="form.profile.language"
              @input="onInput('lang')")
            v-multiselect.mb-3(
              :label="$t('base.user_groups')"
              :options="localGroups"
              :loading="loadingGroups"
              v-model="form.groups"
              @input="onInput('user_groups')")
            v-multiselect.mb-3(
              :label="$t('base.farms')"
              :options="localFarms"
              :loading="loadingFarms"
              v-model="form.farms"
              @input="onInput('farms')")
            v-input(
              :label="$t('base.glob_info')"
              :hint="errorMessage('about')"
              :invalid="!!errorMessage('about')"
              v-model="form.profile.about"
              @input="onInput('about')")
    v-card-actions
      v-btn(
        :loading="loading"
        @click="onSubmit")
        | {{ isEditMode ? $t('base.edit') : $t('base.add') }}
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'

const LANGS = [
  { id: 0, name: 'English', key: 'en' },
  { id: 1, name: 'Українська', key: 'uk' },
  { id: 2, name: 'Русский', key: 'ru' },
  { id: 3, name: 'Polish', key: 'pl' },
  { id: 4, name: 'Nederlands', key: 'nl' },
  { id: 5, name: 'Deutschland', key: 'de' },
  { id: 6, name: 'France', key: 'fr' },
  { id: 7, name: 'Italia', key: 'it' },
  { id: 8, name: 'România', key: 'ro' },
  { id: 9, name: 'USA', key: 'us' },
]

export default {
  name: 'DetailModal',

  props: {
    selected: {
      type: Object,
      default: null
    },
    langs: {
      type: Array,
      default: () => LANGS
    }
  },

  mounted () {
    this.initializeBasicFields()
    this.loadData()
  },

  data: () => ({
    form: {
      first_name: null,
      last_name: null,
      phone: null,
      email: null,
      is_active: true,
      profile: {
        about: '',
        language: null
      },
      farms: [],
      groups: []
    },
    errors: {
      first_name: null,
      last_name: null,
      email: null
    },
    localFarms: [],
    localGroups: [],
    loadingFarms: false,
    loadingGroups: false,
    loading: false
  }),

  computed: {
    isEditMode () {
      return this.selected !== null
    }
  },

  watch: {
    farms: {
      handler (newFarms, oldFarms) {
        if (newFarms && newFarms.length > 0) {
          if (this.selected && this.selected.farms) {
            this.form.farms = this.selected.farms
              .map(selectedFarm => {
                return newFarms.find(farm => farm.id === selectedFarm.id)
              })
              .filter(farm => farm !== undefined)
          } else if (!this.selected) {
            this.form.farms = []
          }
        } else {
          this.form.farms = []
        }
      },
      immediate: true
    },

    groups: {
      handler (newGroups, oldGroups) {
        if (newGroups && newGroups.length > 0) {
          if (this.selected && this.selected.groups) {
            this.form.groups = this.selected.groups
              .map(selectedGroup => {
                return newGroups.find(group => group.id === selectedGroup.id)
              })
              .filter(group => group !== undefined)
          } else if (!this.selected) {
            this.form.groups = []
          }
        } else {
          this.form.groups = []
        }
      },
      immediate: true
    },

    selected: {
      handler (newSelected) {
        if (newSelected) {
          this.initializeForm(newSelected)
        } else {
          this.resetForm()
        }
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('employees', [
      'createEmployee',
      'saveEmployee'
    ]),

    initializeBasicFields () {
      if (this.selected) {
        this.form.first_name = this.selected.first_name
        this.form.last_name = this.selected.last_name
        this.form.phone = this.selected.phone
        this.form.email = this.selected.email
        this.form.is_active = this.selected.is_active
        this.form.profile = { ...this.selected.profile }
      } else {
        this.form.first_name = null
        this.form.last_name = null
        this.form.phone = null
        this.form.email = null
        this.form.is_active = true
        this.form.profile = {
          about: '',
          language: null
        }
        this.form.farms = []
        this.form.groups = []
      }

      this.errors = {
        first_name: null,
        last_name: null,
        email: null
      }
    },

    async loadData () {
      await Promise.all([
        this.loadFarms(),
        this.loadPermissionGroups()
      ])
      this.mapUserFarmsAndGroups()
    },

    async loadFarms () {
      this.loadingFarms = true
      try {
        const { data, error } = await api.farms.list()
        if (!error && data) {
          this.localFarms = data
        }
      } catch (e) {
        console.error('Error loading farms:', e)
      } finally {
        this.loadingFarms = false
      }
    },

    async loadPermissionGroups () {
      this.loadingGroups = true
      try {
        const { data, error } = await api.permissions.litelist()
        if (!error && data) {
          this.localGroups = data
        }
      } catch (e) {
        console.error('Error loading groups:', e)
      } finally {
        this.loadingGroups = false
      }
    },

    mapUserFarmsAndGroups () {
      if (!this.selected) return

      if (this.selected.farms && this.selected.farms.length) {
        this.form.farms = this.selected.farms
          .map(selectedFarm => this.localFarms.find(farm => farm.id === selectedFarm.id))
          .filter(farm => farm !== undefined)
      }

      if (this.selected.groups && this.selected.groups.length) {
        this.form.groups = this.selected.groups
          .map(selectedGroup => this.localGroups.find(group => group.id === selectedGroup.id))
          .filter(group => group !== undefined)
      }
    },

    errorMessage (key) {
      if (this.errors && this.errors[key]) {
        if (this.errors[key][0].includes('numeric')) {
          this.errors[key][0] = ['30']
        } else if (this.errors[key][0].includes('password')) {
          this.errors[key][0] = ['31']
        } else if (this.errors[key][0].includes('15 digits')) {
          this.errors[key][0] = ['phone']
        }

        return this.$t(`server_error_codes.${this.errors[key][0]}`)
      }
      return null
    },

    onInput (key) {
      if (this.errors && this.errors[key]) {
        delete this.errors[key]
      }
    },

    async onSubmit () {
      const payload = {
        ...this.form,
        farms: this.form.farms.map(item => ({ id: item.id })),
        groups: this.form.groups.map(item => ({ id: item.id }))
      }
      if (!this.validateFields()) return
      this.loading = true
      const { errors } = this.isEditMode
        ? await this.saveEmployee({ id: this.selected.id, ...payload })
        : await this.createEmployee(payload)
      if (errors) {
        this.errors = errors
      } else {
        this.$emit('close')
      }
      this.loading = false
    },

    validateFields () {
      let valid = true
      const errorCodes = {
        REQUIRED: '4'
      }
      if (!this.form.first_name) {
        this.errors.first_name = [errorCodes.REQUIRED]
        valid = false
      }

      if (!this.form.last_name) {
        this.errors.last_name = [errorCodes.REQUIRED]
        valid = false
      }

      if (!this.form.email) {
        this.errors.email = [errorCodes.REQUIRED]
        valid = false
      }

      return valid
    }
  }
}
</script>